/**
 * useStateContext — State Context Hook (V102)
 *
 * State context is resolved ONLY from:
 *  1. URL param :stateId (primary — source of truth)
 *  2. /api/states/state-list (secondary — to enrich state metadata)
 *
 * NO localStorage. No stale state. No cross-session contamination.
 *
 * Every navigation to /states/:stateId/* sets the context cleanly.
 * State switches navigate to a new URL — the URL IS the context.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/api/client";

export function useStateContext() {
  const { stateId: urlStateId } = useParams();
  const navigate = useNavigate();

  const [states,     setStates]     = useState([]);
  const [activeState, setActiveStateData] = useState(null);
  const [loading,    setLoading]    = useState(true);

  // Load state list and resolve active state from URL param
  useEffect(() => {
    client.get("/states/state-list")
      .then(r => {
        const stateList = r.data?.states || [];
        setStates(stateList);

        if (urlStateId) {
          // URL param is source of truth — find matching state
          const matched = stateList.find(s => s.state_id === parseInt(urlStateId));
          setActiveStateData(matched || {
            state_id:  parseInt(urlStateId),
            geography: "Loading...",
            name:      "Loading...",
          });
        } else if (stateList.length > 0) {
          // No URL param — use first available (StateEntry should have resolved this)
          setActiveStateData(stateList[0]);
        }
        setLoading(false);
      })
      .catch(() => {
        setStates([]);
        setLoading(false);
      });
  }, [urlStateId]);

  /**
   * Switch to a different state.
   * Updates the URL — the URL becomes the new context. No localStorage.
   */
  const setActiveState = useCallback((state, currentSubPath) => {
    if (!state.state_id) return;

    // Determine current sub-path from URL for continuity
    const path = window.location.pathname;
    const stateMatch = path.match(/\/states\/(\d+)\/(.+)/);
    const subPath = currentSubPath || (stateMatch ? stateMatch[2] : "overview");

    navigate(`/states/${state.state_id}/${subPath}`, { replace: true });
    // Note: activeState will be updated by the useEffect when URL changes
  }, [navigate]);

  return {
    stateId:     activeState?.state_id,
    geography:   activeState?.geography || activeState?.country || "",
    stateName:   activeState?.name || "",
    activeState,
    setActiveState,
    states,
    loading,
    // API query param helper — always uses state_id if available
    stateParams: activeState?.state_id
      ? `?state_id=${activeState.state_id}`
      : activeState?.geography
        ? `?geography=${activeState.geography}`
        : "",
  };
}
